import Fuse from 'fuse.js'
import React, { useEffect, useState } from 'react'
import { Selection, SortDescriptor, TableProps } from 'react-aria-components'

import { Cell, Column, Row, Table, TableBody, TableHeader } from './Table'
import { PaginationConfig, TablePagination, defaultOptions } from './TablePagination'
import { TableSearch } from './TableSearch'

interface Props<T> extends TableProps {
  columns: Array<{
    key: string
    label: string
    width?: number
    allowSorting?: boolean
    [key: string]: unknown
    format?: (value: any) => string
    render?: (row: T) => React.ReactNode
  }>
  rows: T[]
  rowId?: string
  pagination?: PaginationConfig | PaginationConfigSimple | boolean
  search?: SearchConfig | boolean
  sorting?: boolean
  /** Returns selections keys. If 'all' is selected, return the current page selection. The rowId property is required */
  onSelectionChange?(selection: Selection): void
  onLinkAction?(row: T): string
}

type PaginationConfigSimple = {
  page?: string | number
  onPageChange?: (page: number) => void
  setPage?: (page: number) => void
}

type PaginationOptions = PaginationConfig & { pageSize: number }

type SearchConfig = {
  /** List of columns that will be searched. Defined according to Fuse.js keys property. If it's not set, the first column will be used */
  columns?: string[]
  /** Default value for the search input */
  defaultValue?: string
  /** Callback when search query changes */
  onSearchChange?: (query: string) => void
}

/**
 * Provides an abstraction for creating simple tables
 */
export function CompactTable<T extends object>({
  columns,
  rows,
  sorting,
  pagination = true,
  search = true,
  rowId,
  onSelectionChange,
  onLinkAction,
  ...props
}: Props<T>) {
  const rowHeader = columns[0].key // Use the first column as headerRow. We maybe want this to be customizable
  const columnsKeys = columns.map((c) => c.key)
  const [items, setItems] = useState<T[]>([])
  const [filteredItems, setFilteredItems] = useState<T[]>(rows)
  const [page, setPage] = useState(0)
  const [query, setQuery] = useState(typeof search === 'object' ? search.defaultValue || '' : '')

  // Set pagination options
  const isPaginationManagedByUser =
    typeof pagination === 'object' && pagination.setPage !== undefined
  const paginationOptions = extractPaginationOptions(pagination)

  function extractPaginationOptions(
    pagination: PaginationConfig | PaginationConfigSimple | boolean,
  ) {
    if (typeof pagination === 'boolean') {
      return {
        ...defaultOptions,
        page,
        setPage,
        totalCount: filteredItems.length,
      }
    }

    // If only onPageChange is provided, use that as a callback
    // This allows us to support storing the page in the URL
    if ('page' in pagination && 'onPageChange' in pagination) {
      const paginationConfigSimple = pagination as PaginationConfigSimple
      return {
        ...defaultOptions,
        setPage,
        totalCount: filteredItems.length,
        page: Number(paginationConfigSimple.page),
        onPageChange: paginationConfigSimple.onPageChange,
      }
    }

    // For async data allow custom props
    return pagination as PaginationOptions
  }

  useEffect(() => {
    if (!isPaginationManagedByUser) {
      setItems(getPageRows<T>(filteredItems, page, paginationOptions))
    } else {
      setFilteredItems(rows)
      setItems(filteredItems)
    }
  }, [filteredItems, page, query])

  const performSearch = (text: string) => {
    const keys = typeof search === 'object' && search.columns ? search.columns : [rowHeader]

    const fuse = new Fuse(rows, {
      keys: keys,
      threshold: 0.3,
    })

    if (text !== '') {
      const result = fuse.search(text)
      setFilteredItems(result.map((r) => r.item))
    } else {
      setFilteredItems(rows)
    }

    setPage(0)
  }

  useEffect(() => {
    const defaultValue = typeof search === 'object' ? search.defaultValue || '' : ''
    if (defaultValue !== '') {
      performSearch(defaultValue)
    } else {
      setFilteredItems(rows)
    }
  }, [rows])

  // Handle changes to defaultValue prop
  useEffect(() => {
    const defaultValue = typeof search === 'object' ? search.defaultValue || '' : ''
    if (defaultValue !== query) {
      setQuery(defaultValue)
      if (defaultValue !== '') {
        performSearch(defaultValue)
      } else {
        setFilteredItems(rows)
      }
    }
  }, [typeof search === 'object' ? search.defaultValue : undefined])

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    // By default, sort by first column that allows sorting
    column: columns.find((c) => c.allowSorting)?.key || '',
    direction: 'ascending',
  })

  const handleSearch = (text: string) => {
    performSearch(text)
    setQuery(text)

    // Call external search callback if provided
    if (typeof search === 'object' && search.onSearchChange) {
      search.onSearchChange(text)
    }
  }

  const handleSorting = (value: SortDescriptor) => {
    const sortedItems = rows.sort((a: T, b: T) => {
      if (sortDescriptor.column) {
        const d = a[sortDescriptor.column].toString()?.localeCompare(b[sortDescriptor.column])
        return sortDescriptor.direction === 'descending' ? -d : d
      }
    })

    setItems(getPageRows(sortedItems, page, paginationOptions))
    setSortDescriptor(value)
  }

  const handleSelectionChange = (keys: Selection) => {
    // When 'all' is selected return the current page ids
    if (onSelectionChange) {
      if (keys === 'all') {
        if (rowId) {
          return onSelectionChange(new Set(items.map((item) => item[rowId])))
        } else {
          return 'all'
        }
      }

      return onSelectionChange(keys)
    }
  }

  return (
    <div className="CompactTable">
      {search && (
        <TableSearch
          handleSearch={handleSearch}
          defaultValue={typeof search === 'object' ? search.defaultValue : undefined}
        />
      )}
      <Table
        sortDescriptor={sortDescriptor}
        onSortChange={handleSorting}
        onSelectionChange={handleSelectionChange}
        {...props}>
        <TableHeader columns={columns}>
          {(column) => {
            return (
              <Column
                allowsSorting={column.allowSorting ?? sorting}
                width={column.width}
                isRowHeader={column.key === rowHeader}>
                {column.label}
              </Column>
            )
          }}
        </TableHeader>
        <TableBody items={items}>
          {(row) => (
            <Row
              key={row[rowHeader]}
              id={row[rowId || rowHeader]}
              href={onLinkAction && onLinkAction(row)}>
              {columnsKeys.map((cell) => {
                const columnProps = columns.find((c) => c.key === cell)

                return (
                  <Cell key={cell}>
                    {columnProps?.render
                      ? columnProps.render(row)
                      : columnProps?.format
                        ? columnProps.format(row[cell])
                        : row[cell]}
                  </Cell>
                )
              })}
            </Row>
          )}
        </TableBody>
      </Table>
      {pagination && <TablePagination pagination={paginationOptions} />}
    </div>
  )
}

function getPageRows<T>(rows: T[], page: number, paginationConfig: PaginationOptions): T[] {
  const { pageSize } = paginationConfig

  return rows.slice(page * pageSize, (page + 1) * pageSize)
}
